import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle, Clock, Globe } from 'lucide-react';
import api from '../services/api';

const defaultContactInfo = {
  company: { name: "Algoryn", tagline: "Transforming Ideas into Digital Reality", description: "Ready to transform your business? Let's discuss how we can help you achieve your goals." },
  contact: { emails: [{ type: "General", address: "hello@algoryn.com" }], phones: [{ type: "Main", number: "+91 9876543210" }], addresses: [{ type: "Head Office", street: "Phase 2, Indus Valley", area: "Sector 62", city: "Noida", state: "Uttar Pradesh", country: "India", pincode: "201301", primary: true }] },
  businessHours: { timezone: "IST (UTC+5:30)", schedule: [{ day: "Monday - Friday", hours: "9:00 AM - 6:00 PM", isOpen: true }, { day: "Saturday", hours: "10:00 AM - 4:00 PM", isOpen: true }, { day: "Sunday", hours: "Closed", isOpen: false }] },
  responseTime: { email: "24 hours", phone: "Immediate", general: "Within 2 business days" }
};

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [contactInfo, setContactInfo] = useState(defaultContactInfo);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/settings');
        const settings = res.data.settings;
        if (settings.contactEmail || settings.contactPhone || settings.address) {
          setContactInfo({
            ...defaultContactInfo,
            company: { ...defaultContactInfo.company, name: settings.companyName || 'Algoryn' },
            contact: {
              emails: [{ address: settings.contactEmail || 'hello@algoryn.com' }],
              phones: [{ number: settings.contactPhone || '+91 9876543210' }],
              addresses: [{ street: settings.address || 'Noida, India', city: 'Noida', state: 'UP', country: 'India', primary: true }]
            }
          });
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setSubmitStatus('validation_error');
      setTimeout(() => setSubmitStatus(null), 5000);
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await api.post('/contact', formData);
      if (res.data.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  if (loading) {
    return (
      <section id="contact" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="animate-pulse">
            <div className="text-center mb-16">
              <div className="h-12 bg-gray-200 rounded mb-4 w-64 mx-auto"></div>
              <div className="h-6 bg-gray-200 rounded w-96 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-20 bg-slate-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">Get In Touch</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">{contactInfo.company.description}</p>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
            <div className="flex items-center"><Clock className="w-4 h-4 mr-1" /><span>Response: {contactInfo.responseTime.general}</span></div>
            <div className="flex items-center"><Globe className="w-4 h-4 mr-1" /><span>{contactInfo.businessHours.timezone}</span></div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-cyan-400 to-blue-500 p-3 rounded-lg"><Mail className="w-6 h-6 text-white" /></div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Email</h4>
                    <p className="text-gray-600">{contactInfo.contact.emails[0]?.address || 'hello@algoryn.com'}</p>
                    <p className="text-xs text-gray-500 mt-1">Response: {contactInfo.responseTime.email}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-cyan-400 to-blue-500 p-3 rounded-lg"><Phone className="w-6 h-6 text-white" /></div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Phone</h4>
                    <p className="text-gray-600">{contactInfo.contact.phones[0]?.number || '+91 9876543210'}</p>
                    <p className="text-xs text-gray-500 mt-1">Response: {contactInfo.responseTime.phone}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-cyan-400 to-blue-500 p-3 rounded-lg"><MapPin className="w-6 h-6 text-white" /></div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Address</h4>
                    <p className="text-gray-600">{contactInfo.contact.addresses[0]?.street || 'Noida, India'}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Office Hours</h3>
              <div className="space-y-2">
                {contactInfo.businessHours.schedule.map((schedule, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-gray-600">{schedule.day}</span>
                    <span className={`font-semibold ${schedule.isOpen ? 'text-slate-900' : 'text-red-500'}`}>{schedule.hours}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Send us a Message</h3>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                <span className="text-green-800">Message sent! We'll get back to you soon.</span>
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                <span className="text-red-800">Failed to send. Please try again.</span>
              </div>
            )}
            {submitStatus === 'validation_error' && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center">
                <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
                <span className="text-yellow-800">Please fill in all required fields.</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-900 mb-2">Full Name *</label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500" placeholder="Your name" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-900 mb-2">Email *</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500" placeholder="your@email.com" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-900 mb-2">Phone</label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500" placeholder="+91 9876543210" />
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-slate-900 mb-2">Company</label>
                  <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500" placeholder="Your company" />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-slate-900 mb-2">Subject</label>
                <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500" placeholder="Brief subject" />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-900 mb-2">Message *</label>
                <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 resize-none" placeholder="Tell us about your project..." />
              </div>
              <button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center disabled:opacity-70">
                {isSubmitting ? <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>Sending...</> : <><Send className="mr-2 w-5 h-5" /> Send Message</>}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="bg-gradient-to-r from-cyan-400 to-blue-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"><Mail className="w-8 h-8 text-white" /></div>
            <h4 className="text-xl font-bold text-slate-900 mb-2">Email Us</h4>
            <p className="text-gray-600 mb-4">Get in touch via email</p>
            <a href={`mailto:${contactInfo.contact.emails[0]?.address || 'hello@algoryn.com'}`} className="bg-gradient-to-r from-cyan-400 to-blue-500 text-white px-6 py-2 rounded-lg inline-block">Send Email</a>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="bg-gradient-to-r from-cyan-400 to-blue-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"><Phone className="w-8 h-8 text-white" /></div>
            <h4 className="text-xl font-bold text-slate-900 mb-2">Call Us</h4>
            <p className="text-gray-600 mb-4">Speak with our team</p>
            <a href={`tel:${contactInfo.contact.phones[0]?.number || '+919876543210'}`} className="bg-gradient-to-r from-cyan-400 to-blue-500 text-white px-6 py-2 rounded-lg inline-block">Call Now</a>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="bg-gradient-to-r from-cyan-400 to-blue-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"><MapPin className="w-8 h-8 text-white" /></div>
            <h4 className="text-xl font-bold text-slate-900 mb-2">Visit Us</h4>
            <p className="text-gray-600 mb-4">Face-to-face meeting</p>
            <a href="https://maps.app.goo.gl/ZZ6sSfAEk9LiCkyz8" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-cyan-400 to-blue-500 text-white px-6 py-2 rounded-lg inline-block">Get Directions</a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;